import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutableRef } from '@carnica/utils/hooks';
import { useProgressLine } from "../useProgressLine";
import { useProgressTimer } from "../useProgressTimer";
export var useProgressBar = function useProgressBar(params) {
  var _params$duration = params.duration,
    duration = _params$duration === void 0 ? 1000 : _params$duration,
    _params$mode = params.mode,
    mode = _params$mode === void 0 ? 'auto' : _params$mode,
    _params$steps = params.steps,
    stepsProp = _params$steps === void 0 ? 1 : _params$steps,
    _params$activeStep = params.activeStep,
    activeStepProp = _params$activeStep === void 0 ? 0 : _params$activeStep,
    _params$progress = params.progress,
    progressProp = _params$progress === void 0 ? 0 : _params$progress,
    iterative = params.iterative,
    onFinish = params.onFinish,
    _params$isActive = params.isActive,
    isActive = _params$isActive === void 0 ? true : _params$isActive,
    onStepFinish = params.onStepFinish,
    infinite = params.infinite,
    onInit = params.onInit;
  var steps = Math.max(stepsProp, 1);
  var progress = Math.max(Math.min(progressProp, 100), 0);
  var activeStep = Math.max(Math.min(activeStepProp, steps), 0);
  var totalDuration = duration * steps;
  var interval = duration / 10;
  var _useState = useState(activeStep),
    _useState2 = _slicedToArray(_useState, 2),
    currentStep = _useState2[0],
    setCurrentStep = _useState2[1];
  var currentStepRef = useMutableRef(currentStep);
  var containerRef = useRef(null);
  var _useProgressLine = useProgressLine(containerRef, {
      interval: interval,
      steps: steps,
      currentStep: currentStep,
      iterative: iterative
    }),
    size = _useProgressLine.size,
    setProgressOffset = _useProgressLine.setProgressOffset,
    setProgressWidth = _useProgressLine.setProgressWidth,
    setTransition = _useProgressLine.setTransition,
    setProgressWidthByIndex = _useProgressLine.setProgressWidthByIndex;
  var onTimeOver = useCallback(function () {
    if (infinite) {
      setTimeout(function () {
        setProgressWidth(0, true);
        setProgressOffset(0);
      }, interval + 200);
      setTimeout(function () {
        restart();
        setTransition.on();
      }, interval + 300);
    } else {
      onFinish === null || onFinish === void 0 ? void 0 : onFinish();
    }
  }, [infinite, onFinish, isActive]);
  var onInterval = function onInterval(index) {
    setProgressWidthByIndex(index);

    // Игнорируем последующие сценарии если шаг не завершен
    if (index % 1 !== 0 || index % 1 === 0 && index === currentStepRef.current) {
      return;
    }
    timer.pause();
    setTimeout(function () {
      if (ignoreIntervalRef.current) {
        return;
      }
      if (index && (index < steps || infinite)) {
        onStepFinish === null || onStepFinish === void 0 ? void 0 : onStepFinish(Math.round(index));
      }
      if (index !== steps) {
        _changeStep(Math.round(index), true);
      }

      // Необходимо чтоб все обновилось и не было дерганий из-за еще не удаленного transition
      if (index !== steps || infinite) {
        setTimeout(function () {
          resetTimer(totalDuration - duration * index);
        }, 100);
      }
    }, interval);
  };
  var _useProgressTimer = useProgressTimer({
      steps: steps,
      duration: duration,
      onInterval: onInterval,
      onTimeOver: onTimeOver,
      mode: mode,
      isActive: isActive
    }),
    timer = _useProgressTimer.timer,
    resetTimer = _useProgressTimer.resetTimer,
    ignoreIntervalRef = _useProgressTimer.ignoreIntervalRef,
    setIgnoreInterval = _useProgressTimer.setIgnoreInterval;
  var _changeStep = useCallback(function (step, withoutReset) {
    var withReset = mode === 'auto' && !withoutReset;
    if (withReset) {
      // Игнорируем таймер при смене шага
      setIgnoreInterval(true);
    }
    timer.pause();
    setProgressWidthByIndex(step, true, true, true);
    setCurrentStep(step);
    setTimeout(function () {
      setIgnoreInterval(false);
      setTransition.on();
    }, interval);
    if (withReset) {
      setTimeout(function () {
        resetTimer(totalDuration - duration * step);
      }, 100);
    }
  }, [steps, isActive, iterative, duration, infinite]);
  var restart = function restart() {
    setTransition.on();
    if (mode === 'auto') {
      _changeStep(0);
      resetTimer();
    }
  };
  useEffect(function () {
    setCurrentStep(activeStep);
  }, [activeStep]);
  useEffect(function () {
    if (mode === 'manually') {
      setTransition.on();
      setProgressWidthByIndex(currentStep + progress / 100, true);
    }
  }, [size, steps, currentStep, progress, mode, iterative]);
  useEffect(function () {
    if (mode === 'manually') {
      timer.reset();
      if (currentStep === steps - 1 && progress === 100) {
        onFinish === null || onFinish === void 0 ? void 0 : onFinish();
      }
    }
  }, [mode, currentStep, progress, steps]);
  useEffect(restart, [steps, mode, iterative, duration, infinite]);
  useEffect(function () {
    onInit === null || onInit === void 0 ? void 0 : onInit({
      containerRef: containerRef,
      startTimer: timer.start,
      resetTimer: timer.reset,
      pauseTimer: timer.pause,
      isPlaying: function isPlaying() {
        return !!isActive;
      },
      changeStep: function changeStep(i) {
        return _changeStep(i - 1);
      },
      getCurrentStep: function getCurrentStep() {
        return currentStepRef.current;
      }
    });
  }, [_changeStep, isActive]);
  return useMemo(function () {
    return containerRef;
  }, []);
};